import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  shelfApi,
  Shelf,
  ShelfRow,
  CreateShelfPayload,
  UpdateShelfPayload,
  CreateShelfRowPayload,
  UpdateShelfRowPayload,
} from "../apis/shelveApis";

// Validation schemas for JSON payloads
export const CreateShelfPayloadSchema = z.object({
  name: z.string().optional(),
});

const UpdateShelfPayloadSchema = z.object({
  location: z.object({ lat: z.number(), lng: z.number() }).optional(),
  name: z.string().optional(),
  isActive: z.boolean().optional(),
});

export const CreateShelfRowPayloadSchema = z.object({
  outletShelveId: z.string(),

  rackLevelNumbers: z.string().min(1, "Bay Level Numbers is required"),
  rackLevelType: z.string().min(1, "Bay Level Type is required"),
  name: z.string().optional(),
});

export const UpdateShelfRowPayloadSchema = z.object({
  name: z.string().optional(),
  isActive: z.boolean().optional(),

  levels: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string(),
      })
    )
    .optional(),
});

export const UpdateShelfNamePayloadSchema = z.object({
  name: z.string().optional(),
});

// Corrected: Renamed `createMutation` to `useCreateMutation`
const useCreateMutation = <T, P>(
  mutationFn: (payload: P, isFormData?: boolean) => Promise<any>,
  successMessage: string,
  invalidateKeys: string[] = [],
  validator?: z.ZodSchema<P>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: P) => {
      if (validator) {
        validator.parse(payload);
      }
      const res = await mutationFn(payload);
      if (!res.success) {
        throw new Error(res.message || `${successMessage} failed`);
      }
      return res.data;
    },
    onSuccess: (data: T) => {
      console.log(`✅ ${successMessage}:`, data);
      invalidateKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      );
    },
    onError: (error: Error) => {
      console.error(`❌ ${successMessage} failed:`, error.message);
    },
  });
};

// Corrected: The custom hook now takes the 'id' as a parameter directly.
const useCreateIdBasedMutation = <T, P>(
  mutationFn: (id: string, payload: P, isFormData?: boolean) => Promise<any>,
  successMessage: string,
  invalidateKeys: string[] = [],
  validator?: z.ZodSchema<P>,
  id?: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: P) => {
      if (!id) {
        throw new Error("ID is required");
      }
      if (validator) {
        validator.parse(payload);
      }
      const res = await mutationFn(id, payload);
      if (!res.success) {
        throw new Error(res.message || `${successMessage} failed`);
      }
      return res.data;
    },
    onSuccess: (data: T) => {
      console.log(`✅ ${successMessage}:`, data);
      invalidateKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      );
    },
    onError: (error: Error) => {
      console.error(`❌ ${successMessage} failed:`, error.message);
    },
  });
};

export const useShelfCreateMutation = () =>
  useCreateMutation<Shelf, CreateShelfPayload>(
    shelfApi.createShelf,
    "Shelf created successfully",
    ["shelfList", "shelfAllData"],
    CreateShelfPayloadSchema,
  );

export const useShelfCreateFormDataMutation = () =>
  useCreateMutation<Shelf, Record<string, any>>(
    (payload: any) => shelfApi.createShelf(payload, true), // Correctly pass the isFormData flag
    "Shelf created successfully (FormData)",
    ["shelfList", "shelfAllData"],
  );

export const useShelfListData = (page: number, searchFor: string) => {
  return useQuery({
    queryKey: ["shelfList", page, searchFor],
    queryFn: async () => {
      const res = await shelfApi.getShelves(page, searchFor);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch shelves");
      }
      return res.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useFetchShelfAllData = () => {
  return useQuery({
    queryKey: ["shelfAllData"],
    queryFn: async () => {
      const res = await shelfApi.getAllShelves();
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch shelves");
      }
      return res.data;
    },
    // enabled: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useFetchShelfById = (id: string) => {
  return useQuery({
    queryKey: ["shelf", id],
    queryFn: async () => {
      const res = await shelfApi.getShelfById(id);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch shelf");
      }
      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useDownloadShelveBarcode = () =>
  useMutation({
    mutationFn: async ({
      outletShelveId,
    }: {
      outletShelveId: string;
    }) => {
      const response = await shelfApi.downloadShelveBarcode(
        outletShelveId,
      );
      return response.data; // ✅ return the actual data object
    },
  });

export const useShelfUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<Shelf, UpdateShelfPayload>(
    shelfApi.updateShelf,
    "Shelf updated successfully",
    ["shelfList", "shelfAllData", "shelf"],
    UpdateShelfPayloadSchema,
    id,
  );

export const useShelfUpdateFormDataMutation = (id: string) =>
  useCreateIdBasedMutation<Shelf, Record<string, any>>(
    (id, payload) => shelfApi.updateShelfFormData(id, payload), // Correctly pass the isFormData flag
    "Shelf updated successfully (FormData)",
    ["shelfList", "shelfAllData", "shelf"],
    undefined,
    id,
  );

export const useShelfDeleteMutation = (id: string) =>
  useCreateIdBasedMutation<void, Record<string, any>>(
    shelfApi.deleteShelf,
    "Shelf deleted successfully",
    ["shelfList", "shelfAllData"],
    undefined,
    id,
  );

export const useShelfRowCreateMutation = () =>
  useCreateMutation<ShelfRow, CreateShelfRowPayload>(
    shelfApi.createShelfRow,
    "Shelf row created successfully",
    ["shelfRowsAllData"],
    CreateShelfRowPayloadSchema,
  );

export const useShelfRowCreateFormDataMutation = () =>
  useCreateMutation<ShelfRow, Record<string, any>>(
    (payload: any) => shelfApi.createShelfRow(payload, true), // Correctly pass the isFormData flag
    "Shelf row created successfully (FormData)",
    ["shelfRowsAllData"],
  );

export const useFetchShelfRowsAllData = (id: string, warehouseId: string) => {
  return useQuery({
    queryKey: ["shelfRowsAllData", id],
    queryFn: async () => {
      const res = await shelfApi.getShelfRows(id);
      if (!res.success) {
        return [];
      }
      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useFetchShelfRowById = (id: string) => {
  return useQuery({
    queryKey: ["shelfRow", id],
    queryFn: async () => {
      const res = await shelfApi.getShelfRowById(id);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch shelf row");
      }
      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useShelfRowUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRow, UpdateShelfRowPayload>(
    shelfApi.updateShelfRow,
    "Shelf row updated successfully",
    ["shelfRowsAllData", "shelfRow"],
    UpdateShelfRowPayloadSchema,
    id,
  );

export const useShelfRowUpdateFormDataMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRow, Record<string, any>>(
    (id, payload) => shelfApi.updateShelfRowFormData(id, payload), // Correctly pass the isFormData flag
    "Shelf row updated successfully (FormData)",
    ["shelfRowsAllData", "shelfRow"],
    undefined,
    id,
  );

export const useShelfRowDeleteMutation = (id: string) =>
  useCreateIdBasedMutation<void, Record<string, any>>(
    shelfApi.deleteShelfRow,
    "Shelf row deleted successfully",
    ["shelfRowsAllData"],
    undefined,
    id,
  );
export const usePrintLabelsMutation = (id: string) =>
  useCreateIdBasedMutation<void, void>( 
    shelfApi.printLabels,              
    "Labels printed successfully",
    ["shelfRowsAllData"],
    undefined,
    id,
  );
